/**
 * Referral program — web client helpers shared by the dashboard tile,
 * the Settings → Referrals page, the quota-cap modal and the welcome
 * banner. Thin wrappers over /api/referral/* (see backend
 * routes/referral.js + services/referral.service.js).
 *
 * Program in one line: the invited store gets a bonus credit pack when
 * it picks a plan; the referrer gets a bigger pack 3 days after the
 * invited store's first PAID charge (once per store; reversed on a
 * refund / cancellation within 14 days). Bonus credits are used before
 * the monthly plan credits and never change the Shopify bill.
 */
import { apiGet, apiPost } from './api';

export const REFERRALS_ROUTE = '/referrals';

/** Fired (window) whenever the status may have changed, so tiles refetch. */
export const REFERRAL_CHANGED_EVENT = 'autonoma:referral-changed';

let cache = { at: 0, data: null };

/** GET /api/referral/status (cached 30s unless `force`). */
export async function fetchReferralStatus({ force = false } = {}) {
  if (!force && cache.data && Date.now() - cache.at < 30_000) return cache.data;
  const res = await apiGet('/api/referral/status');
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.success) throw new Error(data?.error || `HTTP ${res.status}`);
  cache = { at: Date.now(), data };
  return data;
}

export function invalidateReferralStatus() {
  cache = { at: 0, data: null };
  try { window.dispatchEvent(new CustomEvent(REFERRAL_CHANGED_EVENT)); } catch { /* SSR */ }
}

/** POST /api/referral/share { method } → { link, text, subject, mailto, x, linkedin, whatsapp }. */
export async function shareReferral(method) {
  const res = await apiPost('/api/referral/share', { method });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.success) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

/** Clipboard write with the hidden-textarea fallback (iframes block the API). */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  }
}

/** Share buttons, in display order. */
export const SHARE_CHANNELS = [
  { method: 'copy', label: 'Copy link', icon: '🔗' },
  { method: 'email', label: 'Email', icon: '✉️' },
  { method: 'x', label: 'X (Twitter)', icon: '' },
  { method: 'linkedin', label: 'LinkedIn', icon: 'in' },
  { method: 'whatsapp', label: 'WhatsApp', icon: '💬' },
];

/**
 * Run one share channel end-to-end: fetch the payload (records the
 * share) and open the target. Resolves { copied } for the copy channel.
 */
export async function runShare(method) {
  const payload = await shareReferral(method);
  if (method === 'copy') {
    const copied = await copyText(payload.link);
    return { copied, payload };
  }
  const url = method === 'email' ? payload.mailto : payload[method];
  if (url) {
    // Embedded iframe: top-level navigation for mailto, new tab for web intents.
    if (method === 'email') window.open(url, '_top');
    else window.open(url, '_blank', 'noopener');
  }
  return { copied: false, payload };
}

/** GET /api/referral/welcome → { show, summary, daysLeft, expiresAt, grant }. */
export async function fetchReferralWelcome() {
  const res = await apiGet('/api/referral/welcome');
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.success) return { show: false };
  return data;
}

export async function dismissReferralWelcome() {
  try { await apiPost('/api/referral/welcome/dismiss', {}); } catch { /* best-effort */ }
}

/** Tone for a referral stage key (status badges + tile). */
export function stageTone(key) {
  switch (key) {
    case 'rewarded': return 'success';
    case 'paid_qualified': return 'attention';
    case 'voided': return 'critical';
    case 'trial':
    case 'awaiting_paid': return 'info';
    default: return 'new';
  }
}
